<template>
  <div class="max-w-4xl mx-auto">
    <Head :title="`Cerrar Caja ${cashRegister.name}`" />

    <!-- Header -->
    <div class="flex items-center gap-4 mb-8">
      <button
        class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
        title="Volver atrás"
        @click="back()"
      >
        <i class="fa fa-arrow-left"></i>
      </button>
      <h1 class="text-3xl font-bold">
        <Link
          class="text-teal-600 hover:text-teal-700 transition-colors"
          href="/cash-registers"
        >
          Cajas Registradoras
        </Link>
        <span class="text-gray-400 font-medium mx-2">/</span>
        <span>Cerrar {{ cashRegister.name }}</span>
      </h1>
    </div>

    <!-- Form -->
    <div class="max-w-4xl bg-white rounded-lg shadow-sm border border-gray-200">
      <form @submit.prevent="close">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <!-- Cash Register Info -->
          <div class="w-full mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100 flex flex-wrap justify-between items-center">
            <div>
              <h3 class="text-lg font-semibold text-gray-800 mb-1">{{ cashRegister.name }}</h3>
              <p class="text-sm text-gray-600">{{ cashRegister.description || 'Sin descripción' }}</p>
            </div>
            <div class="text-sm text-gray-700 mt-2 sm:mt-0">
              <span class="font-medium">Abierta por:</span> {{ cashRegister.opened_by?.name || 'Usuario' }}
            </div>
          </div>

          <!-- Expected Balances Reference -->
          <div class="w-full mb-6">
            <h4 class="text-lg font-medium text-gray-800 mb-2">Arqueo y Cierre de Caja</h4>
            <p class="text-sm text-gray-600 mb-4">
              Ingresa el conteo físico de dinero en efectivo que tienes en la gaveta. El sistema comparará tu conteo contra el saldo esperado para determinar si hay sobrante o faltante.
            </p>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <div>
                <p class="text-xs text-blue-700 font-medium uppercase">Saldo Sistema USD</p>
                <p class="text-lg font-bold text-blue-900">${{ Number(cashRegister.current_balance || 0).toFixed(2) }}</p>
              </div>
              <div>
                <p class="text-xs text-blue-700 font-medium uppercase">Saldo Sistema BS</p>
                <p class="text-lg font-bold text-blue-900">Bs. {{ Number(cashRegister.current_balance_bs || 0).toFixed(2) }}</p>
              </div>
              <div>
                <p class="text-xs text-blue-700 font-medium uppercase">Saldo Sistema COP</p>
                <p class="text-lg font-bold text-blue-900">COP {{ Number(cashRegister.current_balance_cop || 0).toLocaleString('es-CO') }}</p>
              </div>
            </div>
          </div>

          <text-input
            v-model="form.closing_balance_usd"
            :error="form.errors.closing_balance_usd"
            class="pb-8 pr-6 w-full lg:w-1/3"
            label="Efectivo Real USD"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
          />

          <text-input
            v-model="form.closing_balance_bs"
            :error="form.errors.closing_balance_bs"
            class="pb-8 pr-6 w-full lg:w-1/3"
            label="Efectivo Real BS"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
          />

          <text-input
            v-model="form.closing_balance_cop"
            :error="form.errors.closing_balance_cop"
            class="pb-8 pr-6 w-full lg:w-1/3"
            label="Efectivo Real COP"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
          />

          <text-input
            v-model="form.notes"
            :error="form.errors.notes"
            class="pb-8 pr-6 w-full"
            label="Observaciones o Notas de Cierre (Opcional)"
            placeholder="Ej: Conteo cuadrado sin novedades, retiro entregado al supervisor..."
          />
        </div>

        <!-- Submit -->
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <Link
            href="/cash-registers"
            class="px-4 py-2 mr-3 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancelar
          </Link>
          <loading-button
            :loading="form.processing"
            class="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
            type="submit"
          >
            Confirmar y Cerrar Caja
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  name: 'CashRegisterClose',

  components: {
    Head,
    Link,
    LoadingButton,
    TextInput
  },

  layout: Layout,

  props: {
    cashRegister: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      form: this.$inertia.form({
        closing_balance_usd: this.cashRegister.current_balance || 0,
        closing_balance_bs: this.cashRegister.current_balance_bs || 0,
        closing_balance_cop: this.cashRegister.current_balance_cop || 0,
        notes: '',
      }),
    }
  },

  methods: {
    back() {
      window.history.back()
    },

    close() {
      if (confirm('¿Estás seguro de registrar el arqueo y cerrar esta caja registradora?')) {
        this.form.post(`/cash-registers/${this.cashRegister.id}/close`, {
          onSuccess: () => {
            this.$inertia.visit('/cash-registers')
          }
        })
      }
    },
  }
}
</script>
